import React, { useEffect, useState } from 'react';
import { useRootStore } from 'store/rootStore';
import { FileUpload, Select } from '@insurancemenu/imenu-components-lib';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { FileType, FILE_TYPE_TITLES } from 'types/carrierFileService';
import { observer } from 'mobx-react-lite';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import {
  AdditionalInfoHeaderStyled,
  FileUploadWrapperStyled,
  SubtitleStyled,
  SponsorFieldStyled,
  ValidationErrorMessageStyled,
} from './styles';
import SponsorSelect from '../SponsorSelect';

export interface UploadSyncFileModalProps {
  show: boolean;
  sponsorId: string;
  onClose: () => void;
}

const UploadSyncFileModal: React.FC<UploadSyncFileModalProps> = observer(({ show, sponsorId, onClose }) => {
  const {
    uploadSyncFilesStore: {
      modalLoading,
      isFileUploadReset,
      date: { from: dateFrom, to: dateTo },
      setSponsorId,
      uploadSyncFiles,
      setIsFileUploadReset,
      getFileUploads,
    },
  } = useRootStore();

  const uploadFileTypesOptions: OptionType[] = Object.entries(FILE_TYPE_TITLES).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  const [sponsor, setSponsor] = useState(sponsorId);
  const [fileType, setFileType] = useState(uploadFileTypesOptions[0].value);
  const [files, setFiles] = useState<AcceptedFiles>({});

  useEffect(() => {
    setSponsor(sponsorId);
  }, [sponsorId]);

  const onModalClose = (): void => {
    setFiles({});
    setFileType(uploadFileTypesOptions[0].value);
    setIsFileUploadReset(true);
    onClose();
    setSponsor(sponsorId);
  };

  const onFileUpload = async (): Promise<void> => {
    await uploadSyncFiles(files, fileType as FileType, sponsor);
    onModalClose();
    if (sponsor) {
      // Change sponsor id on dashboard only after submitting upload modal to avoid rerender on each select onChange
      setSponsorId(sponsor);
      void getFileUploads(dateFrom, dateTo, sponsor);
    }
  };

  const areFilesInOneFormat = [...new Set(Object.keys(files).map((name) => name.split('.').pop()))].length === 1;

  return (
    <ActionModal
      show={show}
      title="Upload Sync Files"
      subtitle={
        <SubtitleStyled>Select sponsor and sync file type. You can upload only 1 file at a time.</SubtitleStyled>
      }
      modalType={ModalType.NO_ICON}
      closeButtonText="Cancel"
      onClose={onModalClose}
      actionButtonText="Upload File"
      onAction={onFileUpload}
      loading={modalLoading}
      modalContent={
        <>
          <AdditionalInfoHeaderStyled>
            <SponsorFieldStyled>
              <SponsorSelect
                value={sponsor}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                  setSponsor(e.target.value);
                }}
              />
            </SponsorFieldStyled>
            <Select
              label="File Type"
              name="fileType"
              options={uploadFileTypesOptions}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                setFileType(e.target.value);
              }}
              value={fileType}
              width="23rem"
              validationPassed={!!fileType}
              validationMessage="Please fill this field"
            />
          </AdditionalInfoHeaderStyled>
          <FileUploadWrapperStyled>
            <FileUpload
              allowMultipleFiles
              fileMimeTypes={[]} // without file type validations
              shouldBeReset={isFileUploadReset}
              onChange={setFiles}
            />
            {Object.keys(files).length > 0 && !areFilesInOneFormat && (
              <ValidationErrorMessageStyled>
                To proceed, please ensure all uploaded files are the same file type
              </ValidationErrorMessageStyled>
            )}
          </FileUploadWrapperStyled>
        </>
      }
      isValid={Object.keys(files).length > 0 && areFilesInOneFormat}
    />
  );
});

export default UploadSyncFileModal;
